import produce from 'immer'
import { createContainer } from 'unstated-next'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { arrayToTree } from '../../arrayToTree'

import {
    AddChildNodeFn,
    CreateNewNodeFn,
    DeleteNodeFn,
    DnDTreesProps,
    TreeData,
    TreeDataUnflattened,
    InsertSiblingBeforeFn,
    InsertParentFn,
    InsertSiblingAfterFn,
    MoveNodeFn,
    SetNodeDraggingIdFn,
    TreeNode,
    TreeNodes,
    TreeNodesUnflattened,
    TreeNodeUnflattened,
    UseDndTreeActionsProps,
    TreeOptions,
    Modes,
    LayoutStyle
} from '../dtos'

const useDnDTree = (props: DnDTreesProps) => {
    const mouseDownRef = useRef(0)
    const [mouseDown, setMouseDown] = useState(0)

    mouseDownRef.current = mouseDown

    document.body.onmousedown = function () {
        setMouseDown((m) => m + 1)
    }
    document.body.onmouseup = function () {
        setMouseDown((m) => m - 1)
        setIsDragValid(null)
        setNodeDraggingId(null)
    }

    const {
        actions: actionsFromServer,
        activeNode,
        components,
        onNodeClicked
    } = props
    const treeOptionsDefault: TreeOptions = {
        canEditTreeStructure: false,
        canExpandCollapse: true
    }

    const treeOptions: TreeOptions = {
        ...treeOptionsDefault,
        ...props.treeOptions
    }
    const [treeDataFromServer, setTreeDataFromServer] = useState<TreeData>(
        props.treeData
    )
    const [
        storedDraggedNodePreviousExpandedStatus,
        setStoredDraggedNodePreviousExpandedStatus
    ] = useState<{
        id: string
        isExpanded: boolean
    }>({ id: '', isExpanded: false })
    const [expandedNodes, setExpandedNodes] = useState<{
        [key: string]: { isExpanded: boolean }
    }>(
        Object.assign(
            {},
            ...props.treeData.nodes.map((item) => {
                return {
                    [item.data.id]: { isExpanded: false }
                }
            })
        )
    )

    const [unflattenedTreeData, setUnflattenedTreeData] = useState<
        TreeDataUnflattened
    >(getUnflattenedTreeData(props.treeData.nodes))
    const [draggingId, setDraggingId] = useState<string | null>('')

    const [treeModes, setTreeModes] = useState<{ dragDropMode: boolean }>({
        dragDropMode: false
    })
    const setDragDropMode = (dragDrop: boolean) => {
        if (treeOptions.canEditTreeStructure) {
            const nextTreeModes = produce(treeModes, (draft) => {
                draft.dragDropMode = dragDrop
            })
            setTreeModes(nextTreeModes)
        }
    }

    // Synchronizes local component state with new data coming from server (props)
    useEffect(() => {
        setTreeDataFromServer(props.treeData)
        setExpandedNodes((expandedNodes) => {
            return Object.assign(
                {},
                ...props.treeData.nodes.map((item) => {
                    return {
                        [item.data.id]: {
                            isExpanded: expandedNodes[item.data.id]
                        }
                    }
                })
            )
        })
    }, [props.treeData])

    // Reconciliates draggingId and expandedNodes
    // It sets bach the dragged node to previous collapsed or expanded status
    useEffect(() => {
        if (draggingId === '') {
            if (storedDraggedNodePreviousExpandedStatus.id !== '') {
                setExpandedNodes((expandedNodes) => {
                    const newExpandedNodes = produce(expandedNodes, (draft) => {
                        draft[storedDraggedNodePreviousExpandedStatus.id] = {
                            isExpanded:
                                storedDraggedNodePreviousExpandedStatus.isExpanded
                        }
                    })

                    return newExpandedNodes
                })
            }
        }
    }, [draggingId])

    const [draggedId, setIsDragValid] = useState<string | null>(null)
    const isDragValidRef = useRef<any>(null)
    isDragValidRef.current = draggedId

    // treeDataFromServer
    useLayoutEffect(() => {
        const nextUnflattenedTreeData = produce(
            treeDataFromServer,
            (draftState) => {
                return getUnflattenedTreeData(draftState.nodes)
            }
        )
        setUnflattenedTreeData(nextUnflattenedTreeData)
        // console.log(
        // 	'AFTER',
        // 	JSON.stringify(
        // 		treeDataFromServer.nodes.map((n, i) => {
        // 			return `id: ${n.data.id}, index: ${i} }`
        // 		}),
        // 		null,
        // 		2
        // 	)
        // )
        // console.log('_______________________________________-')
    }, [treeDataFromServer])

    const setNodeDraggingId: SetNodeDraggingIdFn = (nodeId: string | null) => {
        setDraggingId(nodeId)
    }

    let timeoutBeforeCollapsingNodeChildren: any = null

    // onClick on Node handle
    const onMouseDownHandle = (nodeId: string) => {
        // // setDraggingId(nodeId)
        // collapseNode(nodeId)
        // console.log('HANDLE', nodeId)
        if (treeModes?.dragDropMode && nodeId !== isDragValidRef.current) {
            setIsDragValid(nodeId)
            // console.log('onMouseDown set to true', draggedId)
            timeoutBeforeCollapsingNodeChildren = setTimeout(() => {
                if (isDragValidRef.current) {
                    // console.log('onMouseDown test', isDragValidRef.current)
                    const mouse = mouseDownRef.current !== 0 ? true : false
                    if (mouse) {
                        onMouseDownHandle(nodeId)
                        setStoredDraggedNodePreviousExpandedStatus({
                            id: nodeId,
                            isExpanded: expandedNodes[nodeId].isExpanded
                        })
                        setNodeDraggingId(nodeId)
                    }
                }
            }, 100)
        }
    }

    const onMouseUpHandle = (nodeId: string) => {
        // // setDraggingId(nodeId)
        // collapseNode(nodeId)
        // console.log('HANDLE', nodeId)
        if (treeModes?.dragDropMode) {
            setIsDragValid(null)
            if (timeoutBeforeCollapsingNodeChildren) {
                clearTimeout(timeoutBeforeCollapsingNodeChildren)
            }
            // setStoredDraggedNodePreviousExpandedStatus({
            //     id: null,
            //     isExpanded: expandedNodes[nodeId].isExpanded
            // })
            setNodeDraggingId(null)
        }
    }

    /**   
    const onMouseUp = () => {
        console.log('MouseUp')
        setIsDragValid(false)
        if (timeoutBeforeCollapsingNodeChildren) {
            clearTimeout(timeoutBeforeCollapsingNodeChildren)
        }
    }

    const onMouseDown = () => {
        if (canDragDrop) {
            setIsDragValid(true)
            console.log('onMouseDown set to true', isDragValid)
            timeoutBeforeCollapsingNodeChildren = setTimeout(() => {
                if (isDragValidRef.current) {
                    console.log('onMouseDown test', isDragValidRef.current)
                    onMouseDownHandle(nodeId)
                }
            }, 100)
        }
    }
     */

    const createNewNode: CreateNewNodeFn = (args) => {
        // create new node
        // @ts-ignore
        const newNode: TreeNode =
            props.actions?.createNewNode && props.actions.createNewNode(args)

        // updates collapsedNodes
        setExpandedNodes((collapsedNodes) => {
            const nextCollapsedNodes = produce(collapsedNodes, (draft) => {
                const newCollapsedNode = { isExpanded: false }
                draft[newNode.data.id] = newCollapsedNode
            })
            return nextCollapsedNodes
        })
        return newNode
    }

    /**
     * Add child node
     * @param node
     */
    const addChildNode: AddChildNodeFn = (node) => {
        // Early exit
        if (!node) {
            return
        }

        // console.log('addChildNodeClient LOCAL')

        // create new node
        const child: TreeNode = createNewNode({
            data: { parent: node.data.id, layoutStyle: LayoutStyle.List }
        })

        // add new node as a child locally
        setTreeDataFromServer((treeDataFromServer) => {
            const nextTreeDataFromServer = produce(
                treeDataFromServer,
                (draftState) => {
                    let index
                    // Find index of node's deepest descendant, and insert child after this index
                    const descendants = getDescendantsFromFlatNode(
                        node.data.id,
                        unflattenedTreeData
                    )
                    if (descendants.length > 0) {
                        const deepestDescendantId =
                            // @ts-ignore
                            descendants[descendants.length - 1].data.id
                        const deepestDescendantIndex = treeDataFromServer.nodes.findIndex(
                            (n) => n.data.id === deepestDescendantId
                        )
                        index = deepestDescendantIndex + 1
                    } else {
                        index =
                            treeDataFromServer.nodes.findIndex(
                                (n) => n.data.id === node.data.id
                            ) + 1
                    }
                    // insert child at index
                    draftState.nodes.splice(index, 0, child)
                }
            )
            return nextTreeDataFromServer
        })

        // add child on server
        if (actionsFromServer?.addChildNode) {
            actionsFromServer.addChildNode({
                component: node.data.id,
                newComponent: child.data.id
            })
        } else {
            console.warn(
                'actionsFromServer is not implemented. Please provide a actionsFromServer function'
            )
        }
    }

    /**
     * Delete node and all descendants
     * @param nodeId
     */
    const deleteNode: DeleteNodeFn = (nodeId) => {
        // Find nodeIndex to delete, find descendants count and add to the nodeIndex
        const node = treeDataFromServer.nodes.find((n) => n.data.id === nodeId)
        const nodeIndexToDelete = treeDataFromServer.nodes.findIndex(
            (n) => n.data.id === nodeId
        )

        if (nodeIndexToDelete === -1 || !node) {
            return
        }

        // Delete locally
        setTreeDataFromServer((treeDataFromServer) => {
            const nextTreeDataFromServer = produce(
                treeDataFromServer,
                (draftState) => {
                    const descendantsCount = getDescendantsFromFlatNode(
                        node.data.id,
                        unflattenedTreeData
                    ).length
                    draftState.nodes.splice(
                        nodeIndexToDelete,
                        1 + descendantsCount
                    )
                }
            )
            return nextTreeDataFromServer
        })

        // Delete on server
        if (actionsFromServer?.deleteNode) {
            actionsFromServer.deleteNode(nodeId)
        } else {
            console.warn(
                'deleteNode is not implemented. Please provide a deleteNode function'
            )
        }
    }

    /**
     * Insert sibling before
     * @param args
     */
    const insertSiblingBefore: InsertSiblingBeforeFn = (beforeNode) => {
        // Can't insert a sibling to a node that does not have parent
        if (!beforeNode?.data.parent) {
            return
        }

        const newNode = createNewNode({
            data: {
                layoutStyle: LayoutStyle.List,
                parent: beforeNode.data.parent
            }
        })

        setTreeDataFromServer((treeDataFromServer) => {
            // insert sibling locally
            const nextTreeDataFromServer = produce(
                treeDataFromServer,
                (draftState) => {
                    // find node's index
                    const nodeIndex = treeDataFromServer.nodes.findIndex(
                        (n) => n.data.id === beforeNode.data.id
                    )
                    // insert new node before index
                    const index = nodeIndex - 1
                    draftState.nodes.splice(index, 0, newNode)
                }
            )
            return nextTreeDataFromServer
        })

        // insert sibling  on server
        if (actionsFromServer?.insertSiblingBefore) {
            actionsFromServer.insertSiblingBefore({
                component: beforeNode.data.id,
                newComponent: newNode.data.id
            })
        } else {
            console.warn(
                'actionsFromServer is not implemented. Please provide a actionsFromServer function'
            )
        }
    }

    /**
     * Insert sibling after
     * @param args
     */
    const insertSiblingAfter: InsertSiblingAfterFn = (afterNode) => {
        // Can't insert a sibling to a node that does not have parent
        if (!afterNode?.data.parent) {
            return
        }

        const newNode = createNewNode({
            data: {
                layoutStyle: LayoutStyle.List,
                parent: afterNode.data.parent
            }
        })

        setTreeDataFromServer((treeDataFromServer) => {
            // insert sibling locally
            const nextTreeDataFromServer = produce(
                treeDataFromServer,
                (draftState) => {
                    // find node's index
                    const descendants = getDescendantsFromFlatNode(
                        afterNode.data.id,
                        unflattenedTreeData
                    )
                    let index: number =
                        treeDataFromServer.nodes.findIndex(
                            (n) => n.data.id === afterNode.data.id
                        ) + 1
                    if (descendants.length > 0) {
                        const deepestDescendant =
                            descendants[descendants.length - 1]
                        const deepestDescendantIndex = treeDataFromServer.nodes.findIndex(
                            (n) => n.data.id === deepestDescendant?.data.id
                        )
                        index = deepestDescendantIndex + 1
                    }

                    // insert new node before index
                    draftState.nodes.splice(index, 0, newNode)
                }
            )
            return nextTreeDataFromServer
        })

        // insert sibling  on server
        if (actionsFromServer?.insertSiblingAfter) {
            actionsFromServer.insertSiblingAfter({
                component: afterNode.data.id,
                newComponent: newNode.data.id
            })
        } else {
            console.warn(
                'actionsFromServer is not implemented. Please provide a actionsFromServer function'
            )
        }
    }

    /**
     * Insert parent
     * @param nodeId
     */
    const insertParent: InsertParentFn = (nodeId) => {
        // const node = treeDataFromServer.nodes.find(n => n.data.id === nodeId)

        let newParentId: string = ''

        setTreeDataFromServer((treeDataFromServer) => {
            // Insert locally
            const nextTreeDataFromServer = produce(
                treeDataFromServer,
                (draftState) => {
                    const node = treeDataFromServer.nodes.find(
                        (n) => n.data.id === nodeId
                    )
                    const newParentNode = createNewNode({
                        data: {
                            layoutStyle: LayoutStyle.List,
                            parent: node?.data.parent || ''
                        }
                    })
                    newParentId = newParentNode.data.id
                    const newNode = produce(node, (draftNodeState) => {
                        if (draftNodeState) {
                            draftNodeState.data.parent = newParentNode.data.id
                        }
                    })

                    // Find current node and replace it with new node with updated parent id
                    const nodeIndex = treeDataFromServer.nodes.findIndex(
                        (n) => n.data.id === nodeId
                    )
                    // @ts-ignore
                    draftState.nodes.splice(nodeIndex, 1, newNode)

                    // Insert parent before node's index
                    draftState.nodes.splice(nodeIndex - 1, 0, newParentNode)
                }
            )
            return nextTreeDataFromServer
        })

        if (actionsFromServer?.insertParent) {
            actionsFromServer.insertParent({
                component: nodeId,
                newComponent: newParentId
            })
        } else {
            console.warn(
                'insertParent is not implemented. Please provide a insertParent function'
            )
        }
    }

    const remove = <T>(arr: T[], index: number) => {
        const removedElement = arr[index]
        const newArray = produce(arr, (draft) => {
            draft.splice(index, 1)[0]
            return draft
        })

        return { array: newArray, removedElement }
    }

    const insert = <T>(arr: T[], atIndex: number, element: any) => {
        const newArr = produce(arr, (draft) => {
            draft.splice(atIndex, 0, element)
        })

        return newArr
    }

    const move = <T>(args: {
        arr: T[]
        id: string
        toId: string
        newElement: T
    }) => {
        const { arr, id, toId, newElement } = args

        const fromNode = arr.find((n) => (n as any).data.id === id)
        const toNode = arr.find((n) => (n as any).data.id === toId)
        const fromIndex: number = arr.findIndex(
            (n) => (n as any).data.id === id
        )
        const { array } = remove(arr, fromIndex)
        // @ts-ignore
        const toIndex: number = toId
            ? array?.findIndex((n: any) => (n as any).data.id === toId)
            : array?.length

        // console.log('MOVE START___________________')
        // console.log(
        //     'Before remove node',
        //     arr.map((n, index) => `${(n as any).data.wbsId} | ${index}`),
        //     newElement
        // )
        // console.log(
        //     'After remove node',
        //     array.map((n, index) => `${(n as any).data.wbsId} | ${index}`),
        //     newElement
        // )

        let finalIndex = toIndex
        // @ts-ignore
        if (toIndex === array.length - 1) {
            finalIndex = toIndex + 1
            // console.log('A, CASE move to very end')
        }
        if (fromIndex < toIndex) {
            if ((fromNode as any).data.parent === (toNode as any).data.parent) {
                finalIndex = toIndex + 1
                // console.log('B-1')
            } else {
                finalIndex = toIndex
                // console.log('B-2')
            }
        }
        if (fromIndex === toIndex) {
            if ((fromNode as any).data.parent === (toNode as any).data.parent) {
                finalIndex = toIndex + 1
                // console.log('C-1')
            } else {
                finalIndex = toIndex
                // console.log('C-2')
            }
        }
        if (fromIndex > toIndex) {
            finalIndex = toIndex
            // console.log('D')
        }
        if ((newElement as any).data.parent === toId) {
            // console.log('WARNING, need to do this finalIndex case')
        }
        // console.log('finalIndex', finalIndex)
        // @ts-ignore
        const newArr = insert(array, finalIndex, newElement)
        const sorted = sortByLevels(newArr)
        // console.log(
        //     'MOVE COMPLETED',
        //     sorted.map((n, index) => `${(n as any).data.wbsId} | ${index}`)
        // )
        return sorted
    }

    const sortByLevels = (arr: any[]) => {
        const newArr = produce(arr, (draft) => {
            const levels: any[][] = []
            const root = draft.find((n) => n.data.parent === null)

            const loop = (arr: any[], node: any) => {
                // console.log(node.data.wbsId)
                const level = getHierarchyLevel(arr, (node as any).data.id)
                const arrayIndex = level - 1

                while (!levels[arrayIndex]) {
                    levels.push([])
                }
                levels[arrayIndex].push(node)
                const children = arr.filter(
                    (n) => n.data.parent === node.data.id
                )

                children.map((c) => {
                    loop(arr, c)
                })
            }

            loop(draft, root)
            // console.log('levels', levels)
            // @ts-ignore
            return [].concat.apply([], levels)
        })

        return newArr
    }

    /**
     * Root level = 1. Child of root level = 2. Child of Chilf of root level = 3. Etc.
     * @param arr
     * @param itemId
     */
    const getHierarchyLevel = (arr: any[], itemId: string) => {
        let level = 1
        let item = arr.find((i) => i.data.id === itemId)
        while (item.data.parent) {
            level += 1
            item = arr.find((i) => i.data.id === item.data.parent)
        }
        return level
    }

    /**
     * Move node
     * @param args
     */
    const moveNode: MoveNodeFn = (args: {
        droppedNode: TreeNodeUnflattened
        parentContainerNode: TreeNodeUnflattened
        index: number
    }) => {
        const { index, parentContainerNode } = args
        // @ts-ignore
        const { children, ...droppedNode } = args.droppedNode
        // console.log('___________START MOVE_________________')
        // console.log(
        //     'droppedNode',
        //     parentContainerNode?.data.wbsId,
        //     droppedNode.data.wbsId,
        //     index
        // )
        const isNotDroppingInDescendant = true
        // !isDroppingNodeInDescendant({
        //     index,
        //     droppedNode,
        //     parentContainerNode
        // })

        let droppedOnNode: any = null
        if (parentContainerNode?.children.length === 0) {
            droppedOnNode = treeDataFromServer.nodes.find(
                (n) => n.data.id === parentContainerNode.data.id
            )
            // @ts-ignore
        } else if (index > parentContainerNode?.children?.length - 1) {
            const i = treeDataFromServer.nodes.findIndex(
                (n) =>
                    n.data.id ===
                    parentContainerNode?.children[index - 1]?.data.id
            )

            droppedOnNode =
                treeDataFromServer.nodes[i + 1] || treeDataFromServer.nodes[i]
        } else {
            droppedOnNode = treeDataFromServer.nodes.find(
                // @ts-ignore
                (n) => n.data.id === parentContainerNode.children[index].data.id
            )
        }
        const isNotDroppingAtSameIndex =
            // @ts-ignore
            droppedNode.data.id !== parentContainerNode.children[index]?.data.id

        const allowMoveNode =
            isNotDroppingInDescendant && isNotDroppingAtSameIndex

        if (allowMoveNode) {
            setTreeDataFromServer((treeDataFromServer) => {
                const nextDroppedNode = produce(
                    droppedNode,
                    (draftDroppedNode) => {
                        return {
                            ...draftDroppedNode,
                            data: {
                                ...draftDroppedNode.data,
                                parent: parentContainerNode?.data.id
                            }
                        }
                    }
                )

                const nodes = move<TreeNode>({
                    arr: treeDataFromServer.nodes,
                    id: droppedNode.data.id,
                    toId: droppedOnNode.data.id,
                    // @ts-ignore
                    newElement: nextDroppedNode
                })
                // console.log('AFTER MOVE')
                // console.log(nodes.map((n, i) => `${n.data.wbsId}, ${i}`))

                const nextTreeDataFromServer = {
                    nodes
                }

                return nextTreeDataFromServer
            })
            if (actionsFromServer?.moveNode) {
                actionsFromServer.moveNode({
                    index,
                    nodeKey: droppedNode.data.id,
                    // @ts-ignore
                    parentKey: parentContainerNode.data.id
                })
            } else {
                console.warn(
                    'insertParent is not implemented. Please provide a insertParent function'
                )
            }
        }
        // reset node dragging id
        setNodeDraggingId('')
    }

    const collapseNode = (nodeId: string) => {
        const nextCollapsedNodes = produce(expandedNodes, (draft) => {
            draft[nodeId].isExpanded = false
        })
        setExpandedNodes(nextCollapsedNodes)
    }

    const expandNode = (nodeId: string) => {
        const nextCollapsedNodes = produce(expandedNodes, (draft) => {
            draft[nodeId].isExpanded = true
        })
        setExpandedNodes(nextCollapsedNodes)
    }

    const toggleNode = (nodeId: string) => {
        const nextCollapsedNodes = produce(expandedNodes, (draft) => {
            draft[nodeId].isExpanded = !draft[nodeId].isExpanded
        })
        setExpandedNodes(nextCollapsedNodes)
    }

    const actions: UseDndTreeActionsProps = {
        addChildNode,
        collapseNode,
        expandNode,
        toggleNode,
        createNewNode,
        deleteNode,
        insertSiblingBefore,
        insertParent,
        insertSiblingAfter,
        moveNode,
        setNodeDraggingId
    }

    const modes: Modes = {
        treeModes,
        actions: {
            setDragDropMode
        }
    }
    return {
        actions,
        activeNode,
        events: { onMouseDownHandle, onMouseUpHandle },
        components,
        modes,
        onNodeClicked,
        treeOptions,
        state: {
            expandedNodes,
            draggingId,
            unflattenedTreeData
        }
    }
}

// @ts-ignore
export const DnDTreeContainer = createContainer(useDnDTree)

const getUnflattenedTreeData = (nodes: TreeNodes): TreeDataUnflattened => {
    return {
        nodes: arrayToTree(nodes, {
            dataField: null,
            id: 'data.id',
            parentId: 'data.parent'
        }) as TreeNodesUnflattened
    }
}

const getDeepestDescendant = (node: TreeNodeUnflattened): string => {
    const childenCount = node?.children.length
    // @ts-ignore
    if (childenCount > 0) {
        // @ts-ignore
        return getDeepestDescendant(node.children[childenCount - 1])
    } else {
        // @ts-ignore
        return node.data.id
    }
}

const getDescendants = (
    node: TreeNodeUnflattened,
    descendants?: TreeNodeUnflattened[]
) => {
    if (!node) {
        return []
    }
    let desc = descendants || []
    node.children.map((c) => {
        desc.push(c)
        getDescendants(c, desc)
    })

    return desc
}

const getDescendantsFromFlatNode = (
    nodeId: string,
    treeData: TreeDataUnflattened
) => {
    let treeNodeUnflattened: TreeNodeUnflattened = null
    const getTreeNodeUnflattened = (nodes: TreeNodesUnflattened) => {
        for (let i = 0; i < nodes.length; i++) {
            if (treeNodeUnflattened) {
                break
            }
            // @ts-ignore
            if (nodes[i].data.id === nodeId) {
                treeNodeUnflattened = nodes[i]
                break
            } else {
                // @ts-ignore
                getTreeNodeUnflattened(nodes[i].children)
            }
        }
    }
    getTreeNodeUnflattened(treeData.nodes)

    return getDescendants(treeNodeUnflattened)
}

/**
 * Returns the position index of the node within the flat treeData list
 * @param node
 * @param flatTreeData
 */
const getNodeIndex = (
    node: TreeNodeUnflattened | string,
    flatTreeData: TreeData
) => {
    if (typeof node === 'string') {
        return flatTreeData.nodes.findIndex((n) => n.data.id === node)
    } else {
        return flatTreeData.nodes.findIndex((n) => n.data.id === node?.data.id)
    }
}

const isDroppedAsLastChild = (args: {
    parent: TreeNodeUnflattened
    index: number
}) => {
    const { index, parent } = args
    return parent?.children[index] === undefined
}

const branchNodeQty = (node: TreeNodeUnflattened): number => {
    const nodeCount = 1
    const descendantsCount = getDescendants(node).length
    return descendantsCount + nodeCount
}

// @ts-ignore
const indexAfterNode = (parentIndex): number => {
    return parentIndex + 1
}

const isNodeInserted = (
    node: TreeNodeUnflattened,
    parent: TreeNodeUnflattened
) => {
    return node?.data.parent !== parent?.data.id
}

const arrayToObject = <T>(args: { array: Array<T>; id: any; value: any }) => {
    const { array, id, value } = args

    if (typeof value === 'string') {
        return Object.assign(
            {},
            ...array.map((item) => ({
                [getItemNestedProperty(item, id)]: {
                    data: getItemNestedProperty(item, value)
                }
            }))
        )
    } else {
        return Object.assign(
            {},
            ...array.map((item) => ({
                [getItemNestedProperty(item, id)]: true
            }))
        )
    }
}

const getItemNestedProperty = <T>(item: T, nestedProperty: string): string => {
    // @ts-ignore
    return nestedProperty.split('.').reduce((o, i) => o[i], item)
}
